<template>
  <!-- 广告弹窗容器：更高透明度虚化背景 -->
  <div class="splash-ad-overlay">
    <!-- 广告内容弹窗 -->
    <div class="splash-ad-modal">
      <!-- 呼吸动效关闭按钮 -->
      <button class="close-btn" @click="handleClose">×</button>
      <!-- 轮播容器（相对定位，承载轮播内容） -->
      <div class="carousel-container" ref="carouselRef">
        <!-- 轮播列表（绝对定位，通过translateX实现滚动） -->
        <div 
          class="carousel-list"
          :style="{ transform: `translateX(-${currentIndex * 100}%)`, transition: 'transform 0.5s ease-in-out' }"
        >
          <!-- 遍历所有广告 -->
          <div class="carousel-item" v-for="ad in adList" :key="ad.id">
            <!-- 粒子动效背景 -->
            <div class="particle-container">
              <div 
                v-for="i in 15" 
                :key="`particle-${ad.id}-${i}`"
                class="particle"
                :style="{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  width: `${4 + Math.random() * 8}px`,
                  height: `${4 + Math.random() * 8}px`
                }"
              ></div>
            </div>

            <!-- 广告图片（点击跳转link链接 + 标记广告已显示） -->
            <a 
              v-if="ad.link" 
              :href="ad.link" 
              target="_blank" 
              class="ad-link"
              @click="handleAdClick"
            >
              <div class="ad-image-wrapper">
                <!-- 光效动效层 -->
                <div class="light-effect"></div>
                <!-- 广告主图 -->
                <img 
                  :src="ad.image" 
                  :alt="ad.title || '开屏广告'" 
                  class="ad-image"
                  @error="(e) => handleImageError(e, ad.id)"
                />
              </div>
            </a>
            <!-- 无链接时仅显示图片 -->
            <div v-else class="ad-image-wrapper">
              <div class="light-effect"></div>
              <img 
                :src="ad.image" 
                :alt="ad.title || '开屏广告'" 
                class="ad-image"
                @error="(e) => handleImageError(e, ad.id)"
              />
            </div>
          </div>
        </div>

        <!-- 轮播指示器（可选，显示当前页码） -->
        <div class="carousel-indicators">
          <span 
            v-for="(ad, idx) in adList" 
            :key="`indicator-${ad.id}`"
            class="indicator-dot"
            :class="{ active: idx === currentIndex }"
          ></span>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineEmits, watch } from 'vue';
import axios from 'axios';

// 定义关闭事件
const emit = defineEmits(['close']);

// 响应式数据
const adList = ref([]); // 所有广告列表
const currentIndex = ref(0); // 当前轮播索引
const carouselRef = ref(null); // 轮播容器ref
let carouselTimer = null; // 轮播定时器

// 标记广告已显示（持久化到localStorage）
const markAdShown = () => {
  localStorage.setItem('hasShownSplashAd', 'true');
};

// 获取广告数据（带超时处理）
const fetchAdData = async () => {
  try {
    // 10秒请求超时兜底
    const response = await Promise.race([
      axios.get('/api/ads/splash-ad/'),
      new Promise((_, reject) => setTimeout(() => reject('请求超时'), 10000))
    ]);
    
    if (Array.isArray(response.data) && response.data.length > 0) {
      // 1. 过滤有效广告（至少包含image字段）
      const validAds = response.data.filter(ad => ad.image);
      if (validAds.length === 0) {
        handleClose();
        return;
      }

      // 2. 按id排序，确保id=1为首张，依次类推
      validAds.sort((a, b) => a.id - b.id);
      adList.value = validAds;

      // 3. 定位到id=1的广告（如果存在）
      const firstAdIndex = validAds.findIndex(ad => ad.id === 1);
      if (firstAdIndex !== -1) {
        currentIndex.value = firstAdIndex;
      }

      // 4. 启动轮播（仅当广告数>1时）
      if (validAds.length > 1) {
        startCarousel();
      }
    } else {
      handleClose(); // 无广告数据关闭弹窗
    }
  } catch (error) {
    console.error('获取开屏广告失败:', error);
    handleClose(); // 请求失败关闭弹窗
  }
};

// 启动自动轮播
const startCarousel = () => {
  // 清除已有定时器，避免重复
  if (carouselTimer) clearInterval(carouselTimer);
  // 5秒自动切换
  carouselTimer = setInterval(() => {
    nextAd();
  }, 5000);
};

// 切换到下一个广告
const nextAd = () => {
  currentIndex.value = (currentIndex.value + 1) % adList.value.length;
};

// 图片加载失败处理
const handleImageError = (e, adId) => {
  console.warn(`广告${adId}图片加载失败`, e);
  // 移除加载失败的广告
  adList.value = adList.value.filter(ad => ad.id !== adId);
  // 广告为空则关闭弹窗
  if (adList.value.length === 0) {
    handleClose();
  } else if (currentIndex.value >= adList.value.length) {
    // 修正当前索引（避免越界）
    currentIndex.value = adList.value.length - 1;
  }
};

// 点击广告图片跳转链接时的处理
const handleAdClick = () => {
  markAdShown();
  emit('close');
  document.body.classList.add('ad-closed');
};

// 关闭广告弹窗
const handleClose = () => {
  // 清除轮播定时器
  if (carouselTimer) clearInterval(carouselTimer);
  // 标记广告已显示
  markAdShown();
  // 恢复页面滚动
  document.body.classList.add('ad-closed');
  // 通知父组件隐藏弹窗
  emit('close');
};

// 监听广告列表变化，重新启动轮播
watch(adList, (newList) => {
  if (newList.length > 1) {
    startCarousel();
  } else if (carouselTimer) {
    clearInterval(carouselTimer);
  }
});

// 生命周期：挂载时请求广告数据
onMounted(() => {
  fetchAdData();
});

// 生命周期：卸载时清理定时器
onUnmounted(() => {
  if (carouselTimer) clearInterval(carouselTimer);
});
</script>

<style scoped>
/* 背景层：更高透明度（0.3），轻微虚化 */
.splash-ad-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  background-color: rgba(0, 0, 0, 0.3) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 99999 !important;
  overflow: hidden !important;
}

/* 广告弹窗主体（不裁剪，防止切掉关闭按钮） */
.splash-ad-modal {
  position: relative;
  width: 85%;
  max-width: 600px;
  border-radius: 16px;
  overflow: visible; /* 不裁剪！保证关闭按钮显示 */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  background-color: #fff;
  transform: none !important;
  transition: none !important;
}

/* 轮播容器 —— 这里做圆角裁剪 */
.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px; /* 真正的圆角在这里 */
}

/* 轮播列表（横向排列） */
.carousel-list {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.5s ease-in-out;
}

/* 单张广告项 */
.carousel-item {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  position: relative;
}

/* 粒子动效容器 */
.particle-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

/* 粒子飘带动效 */
.particle {
  position: absolute;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  opacity: 0;
  animation: particleFloat 8s infinite ease-in-out;
}

/* 粒子飘动关键帧 */
@keyframes particleFloat {
  0% {
    transform: translateY(100%) rotate(0deg);
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    transform: translateY(-10%) rotate(360deg);
    opacity: 0;
  }
}

/* 广告图片容器 */
.ad-image-wrapper {
  position: relative;
  display: block;
  z-index: 2;
}

/* 光效动效层 */
.light-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  background-size: 200% 100%;
  animation: lightSweep 6s infinite ease-in-out;
  pointer-events: none;
  z-index: 3;
}

/* 光效扫动关键帧 */
@keyframes lightSweep {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* 广告图片 —— 圆角 */
.ad-image {
  width: 100%;
  height: auto;
  display: block;
  position: relative;
  z-index: 2;
  border-radius: 16px; /* 图片圆角 */
}

/* 链接样式重置 */
.ad-link {
  text-decoration: none;
  display: block;
}

/* 轮播指示器 */
.carousel-indicators {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

/* 指示器圆点 */
.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  transition: background-color 0.3s ease;
}

/* 激活状态圆点 */
.indicator-dot.active {
  background-color: #fff;
  width: 20px;
  border-radius: 4px;
}

/* 呼吸动效关闭按钮 —— 完全正常显示 */
.close-btn {
  position: absolute;
  top: -18px;
  right: -18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  animation: breath 3s infinite ease-in-out;
  transition: background-color 0.2s ease;
}

/* 呼吸动效关键帧 */
@keyframes breath {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
  animation: none;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .splash-ad-modal {
    width: 90%;
  }
  .carousel-container {
    border-radius: 12px;
  }
  .ad-image {
    border-radius: 12px;
  }
  .close-btn {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
  .indicator-dot {
    width: 6px;
    height: 6px;
  }
  .indicator-dot.active {
    width: 16px;
  }
}

/* 兼容webkit内核 */
@supports not (backdrop-filter: blur(6px)) {
  .splash-ad-overlay {
    background-color: rgba(0, 0, 0, 0.4) !important;
  }
}
</style>